import moment from "moment";

import { TaxServiceName } from "~/api/graphql/schema.generated";
import { OTHER_RECURRING_FEE_TAX_SERVICES } from "./constants";
import { formatPeriod } from "./skippedPeriods";

/*
 * A PREVIEW of the invoice line the job will write (SMT-1366), so the operator
 * sees what the customer will read before saving.
 *
 * The line itself is built by kontax-backend
 * (recurringFee/utilities/buildRecurringFeeInvoiceText.ts and
 * TAX_SERVICE_STBVV_TITLE_LABELS), which stays the source of truth. These
 * texts mirror it; if the two drift, the preview is wrong, the invoice is not.
 */

const DATE_FORMAT = "YYYY-MM-DD";

const PRODUCT_NAMES: Partial<Record<TaxServiceName, string>> = {
  [TaxServiceName.RECURRING_TAX_SERVICE]: "smarta Steuerservice",
  [TaxServiceName.RECURRING_TAX_SERVICE_COMPACT]:
    "smarta Steuerservice Kompakt",
  [TaxServiceName.RECURRING_BOOKKEEPING_SERVICE]: "smarta Buchhaltungsservice",
};

const RHYTHM_ADJECTIVES: Record<number, string> = {
  1: "Monatliche",
  3: "Quartalsweise",
  6: "Halbjährliche",
  12: "Jährliche",
};

const RHYTHM_FEE_NOUNS: Record<number, string> = {
  1: "Monatsgebühr",
  3: "Quartalsgebühr",
  6: "Halbjahresgebühr",
  12: "Jahresgebühr",
};

export const STBVV_TITLES: Partial<Record<TaxServiceName, string>> = {
  [TaxServiceName.RECURRING_TAX_SERVICE]: "STBVV § 8 Vorschuss",
  [TaxServiceName.RECURRING_TAX_SERVICE_COMPACT]: "STBVV § 8 Vorschuss",
  [TaxServiceName.RECURRING_BOOKKEEPING_SERVICE]: "STBVV § 33 (2)",
  [TaxServiceName.RECURRING_OTHER_ADVANCE]: "STBVV § 8 Vorschuss",
  [TaxServiceName.RECURRING_OTHER_BOOKKEEPING]: "STBVV § 33 (2)",
  [TaxServiceName.RECURRING_OTHER_FLAT_RATE]: "STBVV § 4 Pauschalvergütung",
};

const describe = (serviceName: TaxServiceName, rhythmInMonths: number) => {
  switch (serviceName) {
    case TaxServiceName.RECURRING_TAX_SERVICE:
    case TaxServiceName.RECURRING_TAX_SERVICE_COMPACT:
      return `${RHYTHM_ADJECTIVES[rhythmInMonths]} Vorauszahlung zur Erstellung der Jahressteuererklärung`;
    case TaxServiceName.RECURRING_BOOKKEEPING_SERVICE:
      return `Buchführung: Kontierung der Belege (${RHYTHM_FEE_NOUNS[rhythmInMonths]})`;
    default:
      return "";
  }
};

export interface InvoicePreviewInput {
  serviceName: TaxServiceName;
  rhythmInMonths: number;
  billingDay: number;
  anchorMonth: number;
  validFrom: string;
  customLabel?: string | null;
  customDescription?: string | null;
  // The last month already billed; the preview starts after it.
  billedThroughMonth?: string | null;
  // No period is billed whose Stichtag lies after Läuft bis.
  validUntil?: string | null;
}

/*
 * The first period the job will bill from today on: the first scheduled month
 * whose Stichtag is on or after both Gilt ab and today, and that starts after
 * the last month already billed.
 */
export const nextBilledPeriod = (
  input: InvoicePreviewInput,
  today: string
): string | null => {
  const earliest = input.validFrom > today ? input.validFrom : today;
  const firstUnbilled = input.billedThroughMonth
    ? moment(input.billedThroughMonth, DATE_FORMAT).add(1, "month")
    : null;
  const fromEarliest = moment(earliest, DATE_FORMAT).startOf("month");
  const month =
    firstUnbilled && firstUnbilled.isAfter(fromEarliest)
      ? firstUnbilled
      : fromEarliest;

  for (let i = 0; i < 36; i += 1, month.add(1, "month")) {
    const offset = month.month() - (input.anchorMonth - 1);
    const scheduled =
      input.rhythmInMonths <= 1 ||
      ((offset % input.rhythmInMonths) + input.rhythmInMonths) %
        input.rhythmInMonths ===
        0;
    const stichtag = month.clone().date(input.billingDay).format(DATE_FORMAT);
    if (input.validUntil && stichtag > input.validUntil) {
      return null;
    }
    if (scheduled && stichtag >= earliest) {
      return month.format(DATE_FORMAT);
    }
  }
  return month.format(DATE_FORMAT);
};

// null when Läuft bis ends the fee before another period is due.
export const buildInvoicePreview = (
  input: InvoicePreviewInput,
  today: string
): { title: string; text: string } | null => {
  const period = nextBilledPeriod(input, today);
  if (!period) {
    return null;
  }
  const isOther = OTHER_RECURRING_FEE_TAX_SERVICES.includes(input.serviceName);
  const productName = isOther
    ? input.customLabel?.trim() || "…"
    : PRODUCT_NAMES[input.serviceName];
  const description = isOther
    ? input.customDescription?.trim()
    : describe(input.serviceName, input.rhythmInMonths);

  const text = [
    formatPeriod(period, input.rhythmInMonths),
    productName,
    description,
  ]
    .filter(Boolean)
    .join(" | ");

  return { title: STBVV_TITLES[input.serviceName] ?? "", text };
};
